import {
  Field,
  Input,
  ProgressBar,
  Text,
  Tooltip,
} from "@fluentui/react-components";
import {
  Add16Regular,
  ArrowClockwise16Regular,
  ArrowDownload16Regular,
  Edit16Regular,
  Link16Regular,
  LockClosed16Regular,
  LockOpen16Regular,
} from "@fluentui/react-icons";
import {
  closeDiagnostic,
  closeDistributions,
  createDiagnosticDistribution,
  exportAnswersOfDistributionsCSV,
  getDiagnosticById,
  getPublicDiagnosticUrl,
  openDistributions,
  updateDistributionLabel,
  updateDistributionSpeech,
} from "apps/diagnostics/api";
import { IDiagnosticDistribution } from "apps/diagnostics/models/IDiagnosticDistribution";
import { ConstantValues } from "common/constants";
import { ActionDialog } from "components/actionDialog/ActionDialog";
import { AdminListPage } from "components/adminList/AdminListPage";
import {
  IAdminFilterConfig,
  IAdminListAction,
  IAdminRowAction,
} from "components/adminList/AdminListPage.types";
import { useAdminColumnStyles } from "components/adminList/AdminTable.styles";
import { CustomToggle } from "components/customToggle/CustomToggle";
import { ITableColumn } from "models/ITableColumn";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { formatDate } from "utils/generalUtils";
import { updateDiagnosticInSlice } from "../../../../redux/diagnosticsSlice";
import { showToast } from "../../../../redux/toastActions";
import { CopiedLinkDialog } from "./copiedLinkDialog/CopiedLinkDialog";
import { CreateDistributionDialog } from "./createDistributionDialog/CreateDistributionDialog";
import { EditDistributionDialog } from "./editDistributionDialog/EditDistributionDialog";
import { useStyles } from "./manageDistributions.styles";
import { IManageDistributionsProps } from "./manageDistributions.types";
import { QRCodeModal } from "./qrCodeModal/QRCodeModal";

const SORTING_CACHE_KEY = ConstantValues.SORTING_OPTIONS_CACHE_KEY;
const SORTING_SCOPE = "DiagnosticDistributions";
const ITEMS_PER_PAGE = 10;
const SHIMMER_ROWS = 3;

const buildExternalClientUrl = (origin: string, urlPath: string): string =>
  `${origin.replace("aims.", "diagnostics.")}/external-diagnostics/${urlPath}/question/next`;

const readSorting = (): { column: string; direction: string } | null => {
  try {
    const raw = localStorage.getItem(SORTING_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed?.[SORTING_SCOPE] ?? null;
  } catch {
    return null;
  }
};

const writeSorting = (column: string, descending: boolean): void => {
  try {
    const raw = localStorage.getItem(SORTING_CACHE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    parsed[SORTING_SCOPE] = { column, direction: descending ? "desc" : "asc" };
    localStorage.setItem(SORTING_CACHE_KEY, JSON.stringify(parsed));
  } catch (e) {
    console.error("Error writing sorting:", e);
  }
};

export const ManageDistributions = (
  props: IManageDistributionsProps,
): JSX.Element => {
  const {
    diagnosticId,
    distributions,
    respondents,
    objectives,
    onDistributionsChange,
    onRefreshRespondents,
  } = props;
  const dispatch = useDispatch();
  const styles = useStyles();
  const columnStyles = useAdminColumnStyles();

  const [showCloseAllDialog, setShowCloseAllDialog] = useState<boolean>(false);
  const [showCloseDialog, setShowCloseDialog] = useState<boolean>(false);
  const [showOpenDialog, setShowOpenDialog] = useState<boolean>(false);
  const [showCreateDialog, setShowCreateDialog] = useState<boolean>(false);
  const [speechEnabled, setSpeechEnabled] = useState<boolean>(true);
  const [label, setLabel] = useState<string>(ConstantValues.EMPTY_STRING);
  const [maxConnections, setMaxConnections] = useState<number>(500);
  const [editingLabelId, setEditingLabelId] = useState<string | null>(null);
  const [editingLabelValue, setEditingLabelValue] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [editingLabelError, setEditingLabelError] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );

  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const [editingItem, setEditingItem] =
    useState<IDiagnosticDistribution | null>(null);
  const [editLabel, setEditLabel] = useState<string>(
    ConstantValues.EMPTY_STRING,
  );
  const [editSpeechEnabled, setEditSpeechEnabled] = useState<boolean>(true);
  const [editMaxConnections, setEditMaxConnections] = useState<number>(500);
  const [editIsLoading, setEditIsLoading] = useState<boolean>(false);

  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const isExportingRef = React.useRef<boolean>(false);
  const [isRefreshing, setIsRefreshing] = useState<boolean>(false);
  const [copiedDialogOpen, setCopiedDialogOpen] = useState(false);
  const [qrCodeModalOpen, setQrCodeModalOpen] = useState(false);
  const [qrCodeUrl, setQrCodeUrl] = useState<string>("");
  const hideCopiedTimeoutRef = React.useRef<number | null>(null);
  const dialogTriggerRef = React.useRef<HTMLElement | null>(null);
  const labelEditCancelledRef = React.useRef(false);

  const saveFocusAndRun = React.useCallback((fn: () => void) => {
    dialogTriggerRef.current = document.activeElement as HTMLElement;
    fn();
  }, []);

  const restoreFocus = React.useCallback(() => {
    dialogTriggerRef.current?.focus();
    dialogTriggerRef.current = null;
  }, []);

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      await onRefreshRespondents();
    } catch {
      dispatch(showToast("Failed to refresh responses", "error"));
    } finally {
      setIsRefreshing(false);
    }
  }, [onRefreshRespondents, dispatch]);

  const [sortColumn, setSortColumn] = useState<string | null>(
    readSorting()?.column ?? null,
  );
  const [isSortDescending, setIsSortDescending] = useState<boolean>(
    readSorting()?.direction === "desc",
  );

  const [closedFilter, setClosedFilter] = useState<string>("all");
  const [pageIndex, setPageIndex] = useState(0);
  const [items, setItems] = useState<IDiagnosticDistribution[]>(distributions);

  const [itemToClose, setItemToClose] =
    useState<IDiagnosticDistribution | null>(null);
  const [itemToOpen, setItemToOpen] = useState<IDiagnosticDistribution | null>(
    null,
  );
  const [speechTogglePending, setSpeechTogglePending] = useState<{
    distribution: IDiagnosticDistribution;
    checked: boolean;
  } | null>(null);

  const handleToggleSpeechConfirmed = useCallback(async () => {
    if (!speechTogglePending) return;
    const { distribution, checked } = speechTogglePending;
    setSpeechTogglePending(null);
    restoreFocus();

    const updatedItems = items.map((d) =>
      d.distributionId === distribution.distributionId
        ? { ...d, speechEnabled: checked }
        : d,
    );
    setItems(updatedItems);

    try {
      await updateDistributionSpeech({
        diagnosticId,
        distributionId: distribution.distributionId,
        speechEnabled: checked,
      });
      onDistributionsChange(updatedItems);
    } catch (err) {
      console.error("Failed to update speech:", err);
      setItems((prev) =>
        prev.map((d) =>
          d.distributionId === distribution.distributionId
            ? { ...d, speechEnabled: !checked }
            : d,
        ),
      );
      dispatch(showToast("Failed to update speech setting", "error"));
    }
  }, [
    speechTogglePending,
    diagnosticId,
    dispatch,
    items,
    onDistributionsChange,
  ]);

  const handleSaveLabel = useCallback(
    async (distribution: IDiagnosticDistribution) => {
      const newLabel = editingLabelValue.trim();
      if (!newLabel) {
        setEditingLabelError("Name is required.");
        return;
      }
      if (newLabel.length > 255) {
        setEditingLabelError("Name must not exceed 255 characters.");
        return;
      }
      setEditingLabelError(ConstantValues.EMPTY_STRING);
      setEditingLabelId(null);
      setEditingLabelValue(ConstantValues.EMPTY_STRING);
      restoreFocus();
      if (
        newLabel === (distribution.label ?? ConstantValues.EMPTY_STRING).trim()
      )
        return;

      const updatedItems = items.map((d) =>
        d.distributionId === distribution.distributionId
          ? { ...d, label: newLabel }
          : d,
      );
      setItems(updatedItems);
      try {
        await updateDistributionLabel({
          diagnosticId,
          distributionId: distribution.distributionId,
          label: newLabel,
          maxConnections: distribution.maxConnections,
        });
        onDistributionsChange(updatedItems);
      } catch (err) {
        console.error("Failed to update label:", err);
        setItems((prev) =>
          prev.map((d) =>
            d.distributionId === distribution.distributionId
              ? { ...d, label: distribution.label }
              : d,
          ),
        );
        dispatch(showToast("Failed to update distribution name", "error"));
      }
    },
    [
      editingLabelValue,
      diagnosticId,
      dispatch,
      restoreFocus,
      items,
      onDistributionsChange,
    ],
  );

  const handleEditItem = useCallback(
    (distribution: IDiagnosticDistribution) => {
      saveFocusAndRun(() => {
        setEditingItem(distribution);
        setEditLabel(distribution.label ?? ConstantValues.EMPTY_STRING);
        setEditSpeechEnabled(distribution.speechEnabled ?? true);
        setEditMaxConnections(distribution.maxConnections);
        setShowEditDialog(true);
      });
    },
    [saveFocusAndRun],
  );

  const handleSaveEdit = useCallback(async () => {
    if (!editingItem || !diagnosticId) return;
    setEditIsLoading(true);

    const labelChanged =
      editLabel.trim() !==
      (editingItem.label ?? ConstantValues.EMPTY_STRING).trim();
    const maxChanged = editMaxConnections !== editingItem.maxConnections;
    const speechChanged = editSpeechEnabled !== editingItem.speechEnabled;

    const snapshot = items;
    const updatedItem: IDiagnosticDistribution = {
      ...editingItem,
      label: editLabel.trim(),
      speechEnabled: editSpeechEnabled,
      maxConnections: editMaxConnections,
    };
    const updatedItems = items.map((d) =>
      d.distributionId === editingItem.distributionId ? updatedItem : d,
    );
    setItems(updatedItems);

    try {
      if (labelChanged || maxChanged) {
        await updateDistributionLabel({
          diagnosticId,
          distributionId: editingItem.distributionId,
          label: editLabel.trim(),
          maxConnections: editMaxConnections,
        });
      }
      if (speechChanged) {
        await updateDistributionSpeech({
          diagnosticId,
          distributionId: editingItem.distributionId,
          speechEnabled: editSpeechEnabled,
        });
      }
      onDistributionsChange(updatedItems);
    } catch (err) {
      console.error("Failed to update distribution:", err);
      setItems(snapshot);
      dispatch(showToast("Failed to update distribution", "error"));
    } finally {
      setEditIsLoading(false);
      setShowEditDialog(false);
      setEditingItem(null);
      setEditLabel(ConstantValues.EMPTY_STRING);
      setEditSpeechEnabled(true);
      setEditMaxConnections(500);
      restoreFocus();
    }
  }, [
    editingItem,
    editLabel,
    editSpeechEnabled,
    editMaxConnections,
    diagnosticId,
    items,
    dispatch,
    onDistributionsChange,
    restoreFocus,
  ]);

  const columns: ITableColumn<IDiagnosticDistribution>[] = useMemo(
    () => [
      {
        columnId: "label",
        displayValue: "Name",
        renderHeaderCell: () => "Name",
        renderCell: (distribution) => {
          if (editingLabelId === distribution.distributionId) {
            return (
              <Field
                validationState={editingLabelError ? "error" : undefined}
                validationMessage={editingLabelError || undefined}
                aria-label="Distribution name field"
              >
                <Input
                  value={editingLabelValue}
                  autoFocus
                  aria-label="Distribution name"
                  onChange={(_, data) => {
                    if (editingLabelError && data.value.trim())
                      setEditingLabelError(ConstantValues.EMPTY_STRING);
                    setEditingLabelValue(data.value);
                  }}
                  onBlur={() => {
                    if (labelEditCancelledRef.current) {
                      labelEditCancelledRef.current = false;
                      return;
                    }
                    handleSaveLabel(distribution);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      labelEditCancelledRef.current = true;
                      dialogTriggerRef.current = null;
                      handleSaveLabel(distribution);
                    }
                    if (e.key === "Escape") {
                      labelEditCancelledRef.current = true;
                      setEditingLabelId(null);
                      setEditingLabelValue(ConstantValues.EMPTY_STRING);
                      setEditingLabelError(ConstantValues.EMPTY_STRING);
                      restoreFocus();
                    }
                  }}
                  size="small"
                />
              </Field>
            );
          }
          return (
            <span
              className={
                distribution.label
                  ? styles.labelCell
                  : styles.labelCellPlaceholder
              }
              title="Click to edit name"
              onClick={() => {
                setEditingLabelError(ConstantValues.EMPTY_STRING);
                setEditingLabelId(distribution.distributionId);
                setEditingLabelValue(
                  distribution.label ?? ConstantValues.EMPTY_STRING,
                );
              }}
            >
              {distribution.label || "Add name..."}
            </span>
          );
        },
        compare: (a, b) =>
          (a.label ?? ConstantValues.EMPTY_STRING).localeCompare(
            b.label ?? ConstantValues.EMPTY_STRING,
          ),
        className: columnStyles.normal,
      },
      {
        columnId: "user",
        displayValue: "Author",
        renderHeaderCell: () => "Author",
        renderCell: (diagnosticDistribution) => (
          <>{diagnosticDistribution.user.displayName}</>
        ),
        compare: (a, b) => a.user.displayName.localeCompare(b.user.displayName),
        className: columnStyles.medium,
      },
      {
        columnId: "createdAt",
        displayValue: "Created",
        renderHeaderCell: () => "Created",
        renderCell: (diagnosticDistribution) => (
          <Tooltip
            relationship="label"
            content={formatDate(diagnosticDistribution.createdAt)}
          >
            <span>{formatDate(diagnosticDistribution.createdAt)}</span>
          </Tooltip>
        ),
        compare: (a, b) => a.createdAt - b.createdAt,
        className: columnStyles.medium,
      },
      {
        columnId: "respondents",
        displayValue: "Respondents",
        renderHeaderCell: () => "Respondents",
        renderCell: (diagnosticDistribution) => {
          const count = (
            respondents[diagnosticDistribution.distributionId]?.users ?? []
          ).length;
          const max = diagnosticDistribution.maxConnections || 1500;
          const ratio = Math.min(count / max, 1);
          const pct = max > 0 ? Math.round((count / max) * 100) : 0;
          const pctLabel = pct < 1 && count > 0 ? "<1%" : `${pct}%`;
          return (
            <div className={styles.progressCell}>
              <div className={styles.progressBarRow}>
                <ProgressBar
                  value={ratio}
                  max={1}
                  thickness="large"
                  color={
                    ratio >= 0.8 ? "error" : ratio >= 0.5 ? "warning" : "brand"
                  }
                  className={styles.progressBar}
                />
                <Text
                  size={200}
                  weight="semibold"
                  className={
                    ratio >= 0.8
                      ? styles.pctError
                      : ratio >= 0.5
                        ? styles.pctWarning
                        : styles.pctBrand
                  }
                >
                  {pctLabel}
                </Text>
              </div>
              <Text size={200} className={styles.progressFraction}>
                {count.toLocaleString()} / {max.toLocaleString()}
              </Text>
            </div>
          );
        },
        compare: (a, b) => {
          const ac = (respondents[a.distributionId]?.users ?? []).length;
          const bc = (respondents[b.distributionId]?.users ?? []).length;
          return ac - bc;
        },
        className: columnStyles.normal,
      },
      {
        columnId: "speechEnabled",
        displayValue: "Speech",
        renderHeaderCell: () => "Speech",
        renderCell: (diagnosticDistribution) => (
          <CustomToggle
            checked={diagnosticDistribution.speechEnabled ?? true}
            onChange={(checked) =>
              saveFocusAndRun(() =>
                setSpeechTogglePending({
                  distribution: diagnosticDistribution,
                  checked,
                }),
              )
            }
          />
        ),
        compare: (a, b) =>
          Number(a.speechEnabled ?? true) - Number(b.speechEnabled ?? true),
        className: columnStyles.narrow,
      },
      {
        columnId: "isClosed",
        displayValue: "Closed",
        renderHeaderCell: () => "Closed",
        renderCell: (diagnosticDistribution) => (
          <>{diagnosticDistribution.isClosed ? "Yes" : "No"}</>
        ),
        compare: (a, b) => Number(a.isClosed) - Number(b.isClosed),
        className: columnStyles.medium,
      },
    ],
    [
      respondents,
      columnStyles,
      editingLabelId,
      editingLabelValue,
      editingLabelError,
      handleSaveLabel,
    ],
  );

  const filters: IAdminFilterConfig[] = useMemo(
    () => [
      {
        label: "Closed",
        options: [
          { key: "all", label: "All Statuses" },
          { key: "yes", label: "Closed" },
          { key: "no", label: "Open" },
        ],
        selectedKey: closedFilter,
        onChange: (key: string) => {
          setClosedFilter(key);
          setPageIndex(0);
        },
        listboxClassName: styles.filterListbox,
        className: styles.filterDropdown,
      },
    ],
    [closedFilter],
  );

  useEffect(() => {
    setItems(distributions);
  }, [distributions]);

  useEffect(() => {
    return () => {
      if (hideCopiedTimeoutRef.current) {
        window.clearTimeout(hideCopiedTimeoutRef.current);
      }
    };
  }, []);

  const onSortChange = (columnKey: string, descending: boolean): void => {
    setSortColumn(columnKey);
    setIsSortDescending(descending);
    writeSorting(columnKey, descending);
  };

  const filteredItems = useMemo(() => {
    if (closedFilter === "all") return items;
    const wantClosed = closedFilter === "yes";
    return items.filter((d) => d.isClosed === wantClosed);
  }, [items, closedFilter]);

  useEffect(() => {
    setSelectedIds((prev) => {
      if (prev.size === 0) return prev;
      const visible = new Set(filteredItems.map((d) => d.distributionId));
      const next = new Set([...prev].filter((id) => visible.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [filteredItems]);

  const sortedItems = useMemo(() => {
    const base = [...filteredItems];
    if (sortColumn) {
      const column = columns.find((c) => c.columnId === sortColumn);
      if (column?.compare)
        base.sort(
          (a, b) => column.compare!(a, b) * (isSortDescending ? -1 : 1),
        );
    }
    return base;
  }, [filteredItems, sortColumn, isSortDescending, columns]);

  const paginatedItems = useMemo(() => {
    const start = pageIndex * ITEMS_PER_PAGE;
    return sortedItems.slice(start, start + ITEMS_PER_PAGE);
  }, [sortedItems, pageIndex]);

  const pageCount = Math.max(1, Math.ceil(sortedItems.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (pageIndex >= pageCount) setPageIndex(Math.max(0, pageCount - 1));
  }, [pageIndex, pageCount]);

  const handleCopyLinkAsQR = async (distribution: IDiagnosticDistribution) => {
    try {
      setQrCodeModalOpen(true);
      const publicUrl = await getPublicDiagnosticUrl({
        diagnosticId,
        distributionId: distribution.distributionId,
      });

      const url = buildExternalClientUrl(
        window.location.origin,
        publicUrl?.shortPath ?? ConstantValues.EMPTY_STRING,
      );

      // Show QR code modal (QR will be auto-copied when modal opens)
      setQrCodeUrl(url);
    } catch (err) {
      setQrCodeUrl("");
      console.error("Failed to generate QR code", err);
      dispatch(showToast("Failed to generate QR code", "error"));
    }
  };

  const handleCloseItem = useCallback(
    (distribution: IDiagnosticDistribution) => {
      saveFocusAndRun(() => {
        setItemToClose(distribution);
        setShowCloseDialog(true);
      });
    },
    [saveFocusAndRun],
  );

  const handleOpenItem = useCallback(
    (distribution: IDiagnosticDistribution) => {
      saveFocusAndRun(() => {
        setItemToOpen(distribution);
        setShowOpenDialog(true);
      });
    },
    [saveFocusAndRun],
  );

  const triggerCsvDownload = useCallback((blob: Blob, filename: string) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, []);

  const handleExportItem = async (distribution: IDiagnosticDistribution) => {
    if (!diagnosticId) return;

    try {
      const { blob, filename } = await exportAnswersOfDistributionsCSV({
        diagnosticId,
        distributionIds: [distribution.distributionId],
      });
      triggerCsvDownload(blob, filename);
    } catch (error) {
      console.error("Failed to export CSV:", error);
      dispatch(showToast("Failed to export CSV", "error"));
    }
  };

  const rowActions: IAdminRowAction<IDiagnosticDistribution>[] = useMemo(
    () => [
      {
        icon: <Link16Regular />,
        tooltip: "Copy Link",
        onClick: (item) => handleCopyLinkAsQR(item),
        disabled: (item) => item.isClosed,
      },
      {
        icon: <Edit16Regular />,
        tooltip: "Edit",
        onClick: (item: IDiagnosticDistribution) => handleEditItem(item),
        disabled: (_item: IDiagnosticDistribution) => false,
      },
      {
        icon: <LockClosed16Regular />,
        tooltip: "Close",
        onClick: (item) => handleCloseItem(item),
        disabled: (item) => item.isClosed,
      },
      {
        icon: <LockOpen16Regular />,
        tooltip: "Open",
        onClick: (item) => handleOpenItem(item),
        disabled: (item) => !item.isClosed,
      },
      {
        icon: <ArrowDownload16Regular />,
        tooltip: "Export Results",
        onClick: (item) => handleExportItem(item),
        disabled: (item) =>
          (respondents[item.distributionId]?.users ?? []).length === 0,
      },
    ],
    [
      respondents,
      handleEditItem,
      handleCloseItem,
      handleOpenItem,
      handleExportItem,
      diagnosticId,
      triggerCsvDownload,
    ],
  );

  const headerActions: IAdminListAction[] = useMemo(
    () => [
      {
        label: "Create",
        icon: <Add16Regular />,
        onClick: () => setShowCreateDialog(true),
        appearance: "primary",
        disabled: isLoading || objectives.length === 0,
      },
      {
        label: isRefreshing ? "Refreshing..." : "Refresh",
        icon: <ArrowClockwise16Regular />,
        onClick: handleRefresh,
        appearance: "secondary",
        disabled: isRefreshing,
        tooltip: "Refresh respondent counts",
      },
      {
        label: "Close All",
        icon: <LockClosed16Regular />,
        onClick: () => setShowCloseAllDialog(true),
        disabled: !items.length,
      },
      {
        label: "Export Selected",
        icon: <ArrowDownload16Regular />,
        onClick: async () => {
          if (!diagnosticId || selectedIds.size === 0 || isExportingRef.current)
            return;
          isExportingRef.current = true;
          setIsExporting(true);
          try {
            const { blob, filename } = await exportAnswersOfDistributionsCSV({
              diagnosticId,
              distributionIds: [...selectedIds],
            });
            triggerCsvDownload(blob, filename);
            setSelectedIds(new Set());
          } catch (error) {
            console.error("Failed to export CSV:", error);
            dispatch(showToast("Failed to export CSV", "error"));
          } finally {
            isExportingRef.current = false;
            setIsExporting(false);
          }
        },
        disabled: selectedIds.size === 0 || isExporting,
        loading: isExporting,
      },
    ],
    [
      isLoading,
      isRefreshing,
      handleRefresh,
      objectives.length,
      items.length,
      diagnosticId,
      dispatch,
      selectedIds,
      triggerCsvDownload,
      isExporting,
    ],
  );

  const emptyStateMessage = useMemo(() => {
    return closedFilter !== "all"
      ? "No distributions match the current filter."
      : "No distributions yet";
  }, [closedFilter]);

  const onCloseConfirmed = async () => {
    if (!itemToClose || !diagnosticId) return;
    try {
      await closeDistributions({
        id: diagnosticId,
        distributionIds: [itemToClose.distributionId],
      });
      const updatedItems = items.map((d) =>
        d.distributionId === itemToClose.distributionId
          ? { ...d, isClosed: true }
          : d,
      );
      setItems(updatedItems);
      onDistributionsChange(updatedItems);
    } catch (error) {
      console.error("Error closing distribution:", error);
      dispatch(showToast("Failed to close distribution", "error"));
    } finally {
      setShowCloseDialog(false);
      setItemToClose(null);
      restoreFocus();
    }
  };

  const onOpenConfirmed = async () => {
    if (!itemToOpen || !diagnosticId) return;
    try {
      await openDistributions({
        id: diagnosticId,
        distributionIds: [itemToOpen.distributionId],
      });
      const updatedItems = items.map((d) =>
        d.distributionId === itemToOpen.distributionId
          ? { ...d, isClosed: false }
          : d,
      );
      setItems(updatedItems);
      onDistributionsChange(updatedItems);
    } catch (error) {
      console.error("Error opening distribution:", error);
      dispatch(showToast("Failed to open distribution", "error"));
    } finally {
      setShowOpenDialog(false);
      setItemToOpen(null);
      restoreFocus();
    }
  };

  const handleCloseAll = async () => {
    if (!diagnosticId) return;
    try {
      const updated = await closeDiagnostic({ id: diagnosticId });
      dispatch(updateDiagnosticInSlice(updated));
    } catch (e) {
      console.error("Failed to close diagnostic:", e);
      dispatch(showToast("Failed to close diagnostic", "error"));
    } finally {
      setShowCloseAllDialog(false);
    }
  };

  const handleCreateDistribution = async () => {
    if (!diagnosticId) return;

    setIsLoading(true);
    setShowCreateDialog(false);
    try {
      await createDiagnosticDistribution({
        id: diagnosticId,
        speechEnabled: speechEnabled,
        label: label,
        maxConnections: maxConnections,
      });
      const refreshed = await getDiagnosticById({ id: diagnosticId });
      if (refreshed) {
        setItems(refreshed.distributions ?? []);
        dispatch(updateDiagnosticInSlice(refreshed));
        onDistributionsChange(refreshed.distributions ?? []);
      }
    } catch (err) {
      console.error(err);
      dispatch(showToast("Failed to create link.", "error"));
    } finally {
      setIsLoading(false);
      setSpeechEnabled(true);
      setLabel(ConstantValues.EMPTY_STRING);
      setMaxConnections(500);
    }
  };

  return (
    <div className={styles.container}>
      <AdminListPage<IDiagnosticDistribution>
        title="Distributions"
        items={sortedItems}
        paginatedItems={paginatedItems}
        columns={columns}
        isLoading={isLoading}
        headerActions={headerActions}
        filters={filters}
        rowActions={rowActions}
        actionsWidth="175px"
        getRowId={(item) => item.distributionId}
        sortColumn={sortColumn}
        isSortDescending={isSortDescending}
        onSortChange={onSortChange}
        emptyStateMessage={emptyStateMessage}
        shimmerRows={SHIMMER_ROWS}
        noPadding
        selectedPageIndex={pageIndex}
        pageCount={pageCount}
        totalItemsCount={sortedItems.length}
        itemsPerPage={ITEMS_PER_PAGE}
        onPageChange={setPageIndex}
        highlightedRowId={
          speechTogglePending?.distribution.distributionId ??
          itemToClose?.distributionId ??
          itemToOpen?.distributionId ??
          null
        }
        selectable
        selectedRowIds={selectedIds}
        onSelectionChange={setSelectedIds}
        getRowLabel={(item) => item.label ?? item.distributionId}
        hint={
          <div
            className={styles.hintText}
            aria-live="polite"
            aria-atomic="true"
          >
            <span>
              Click a distribution name to rename it inline. Use the Edit icon
              in the actions column to update all settings.
            </span>
            {selectedIds.size > 0 && (
              <span className={styles.selectionCount}>
                {selectedIds.size} selected
              </span>
            )}
          </div>
        }
      />

      <CopiedLinkDialog
        open={copiedDialogOpen}
        onClose={() => setCopiedDialogOpen(false)}
      />

      <CreateDistributionDialog
        isOpen={showCreateDialog}
        onDismiss={() => {
          setShowCreateDialog(false);
          setSpeechEnabled(true);
          setLabel(ConstantValues.EMPTY_STRING);
          setMaxConnections(500);
        }}
        onConfirm={handleCreateDistribution}
        speechEnabled={speechEnabled}
        onSpeechEnabledChange={setSpeechEnabled}
        label={label}
        onLabelChange={setLabel}
        maxConnections={maxConnections}
        onMaxConnectionsChange={setMaxConnections}
      />

      {showEditDialog && editingItem && (
        <EditDistributionDialog
          isOpen={showEditDialog}
          onDismiss={() => {
            setShowEditDialog(false);
            setEditingItem(null);
            setEditLabel(ConstantValues.EMPTY_STRING);
            setEditSpeechEnabled(true);
            setEditMaxConnections(500);
            restoreFocus();
          }}
          onConfirm={handleSaveEdit}
          label={editLabel}
          onLabelChange={setEditLabel}
          speechEnabled={editSpeechEnabled}
          onSpeechEnabledChange={setEditSpeechEnabled}
          maxConnections={editMaxConnections}
          onMaxConnectionsChange={setEditMaxConnections}
          isLoading={editIsLoading}
        />
      )}

      {showCloseDialog && itemToClose && (
        <ActionDialog
          isVisible={showCloseDialog}
          title="Close Distribution"
          onConfirmation={onCloseConfirmed}
          onDismiss={() => {
            setShowCloseDialog(false);
            setItemToClose(null);
            restoreFocus();
          }}
          confirmationMessage="Close"
          dismissMessage="Cancel"
          spinnerMessage="Closing..."
          message="Are you sure you want to close this distribution? It will stop accepting answers."
        />
      )}

      {showOpenDialog && itemToOpen && (
        <ActionDialog
          isVisible={showOpenDialog}
          title="Open Distribution"
          onConfirmation={onOpenConfirmed}
          onDismiss={() => {
            setShowOpenDialog(false);
            setItemToOpen(null);
            restoreFocus();
          }}
          confirmationMessage="Open"
          dismissMessage="Cancel"
          spinnerMessage="Opening..."
          message="Are you sure you want to open this distribution? It will accept answers again."
        />
      )}

      {showCloseAllDialog && (
        <ActionDialog
          isVisible={showCloseAllDialog}
          title="Close Diagnostic"
          onConfirmation={handleCloseAll}
          onDismiss={() => setShowCloseAllDialog(false)}
          confirmationMessage="Close All"
          dismissMessage="Cancel"
          spinnerMessage="Closing..."
          message="Close this diagnostic? All distribution links will be disabled."
        />
      )}

      {speechTogglePending && (
        <ActionDialog
          isVisible={!!speechTogglePending}
          title="Speech Functionality"
          onConfirmation={handleToggleSpeechConfirmed}
          onDismiss={() => {
            setSpeechTogglePending(null);
            restoreFocus();
          }}
          confirmationMessage="Confirm"
          dismissMessage="Cancel"
          message="Are you sure you want to update the speech settings for this distribution?"
        />
      )}

      <QRCodeModal
        isOpen={qrCodeModalOpen}
        onClose={() => {
          setQrCodeModalOpen(false);
          setQrCodeUrl("");
        }}
        url={qrCodeUrl}
        title="Distribution Link"
      />
    </div>
  );
};
